import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { contractService, Contract, ContractStats } from '../services/contractService';
import type { ContractStatus } from '../services/contractService';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/common/Button';
import { StatusBadge } from '../components/common/StatusBadge';
import { Company } from '../types/auth';
import apiService from '../services/api';

export const ContractGenerationPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [contracts, setContracts] = useState<Contract[]>([]);
    const [stats, setStats] = useState<ContractStats | null>(null);
    const [company, setCompany] = useState<Company | null>(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);

    useEffect(() => {
        loadData();
    }, [user?.companyId]);

    const loadData = async () => {
        if (!user?.companyId) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);

            // Load company data
            const companyResponse = await apiService.getMyCompany();
            if (companyResponse.data.success && companyResponse.data.data) {
                setCompany(companyResponse.data.data);
            }

            // Load contracts and stats
            await loadContracts();
        } catch (error: any) {
            console.error('Ошибка загрузки данных:', error);
            toast.error(error.message || 'Ошибка загрузки данных');
        } finally {
            setLoading(false);
        }
    };

    const loadContracts = async () => {
        try {
            if (!company?.bin) {
                toast.error('Информация о компании не найдена');
                return;
            }

            const [contractsResponse, statsResponse] = await Promise.all([
                contractService.getCompanyContracts(company.bin),
                contractService.getContractStats(company.bin)
            ]);

            if (contractsResponse.success && contractsResponse.data) {
                setContracts(contractsResponse.data);
            }

            if (statsResponse.success && statsResponse.data) {
                setStats(statsResponse.data);
            }

        } catch (error: any) {
            console.error('Ошибка загрузки договоров:', error);
            toast.error(error.message || 'Ошибка загрузки договоров');
        }
    };

    const handleCreateContract = async () => {
        try {
            setGenerating(true);

            if (!company?.bin) {
                toast.error('Информация о компании не найдена');
                return;
            }

            const response = await contractService.createContract({
                contractNumber: `CONTRACT-${Date.now()}`,
                companyData: {
                    bin: company.bin,
                    name: company.name,
                    legalAddress: company.legalAddress || '',
                    actualAddress: company.actualAddress || '',
                    contactPhone: company.contactPhone || '',
                    contactEmail: company.contactEmail || '',
                    directorName: company.directorName || '',
                    bank: company.bank,
                    kbe: company.kbe,
                    bik: company.bik,
                    accountNumber: company.accountNumber
                }
            });

            if (response.success && response.data) {
                toast.success('Договор успешно создан');
                await loadContracts(); // Перезагружаем список
            } else {
                throw new Error(response.message || 'Ошибка создания договора');
            }

        } catch (error: any) {
            console.error('Ошибка создания договора:', error);
            toast.error(error.message || 'Ошибка создания договора');
        } finally {
            setGenerating(false);
        }
    };

    const handleGeneratePdf = async (contractId: number) => {
        try {
            const response = await contractService.generateContractPdf(contractId);

            if (response.success && response.data) {
                toast.success('PDF договора успешно сгенерирован');
                await loadContracts(); // Перезагружаем список
            } else {
                throw new Error(response.message || 'Ошибка генерации PDF');
            }

        } catch (error: any) {
            console.error('Ошибка генерации PDF:', error);
            toast.error(error.message || 'Ошибка генерации PDF');
        }
    };

    const handleDownloadContract = async (contractId: number, contractNumber: string) => {
        try {
            const blob = await contractService.downloadContract(contractId);

            // Создаем ссылку для скачивания
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${contractNumber}.docx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success('Договор скачан');

        } catch (error: any) {
            console.error('Ошибка скачивания договора:', error);
            toast.error(error.message || 'Ошибка скачивания договора');
        }
    };

    const handleCancelContract = async (contractId: number) => {
        if (!window.confirm('Вы уверены, что хотите отменить этот договор?')) {
            return;
        }

        try {
            const response = await contractService.cancelContract(contractId);

            if (response.success && response.data) {
                toast.success('Договор успешно отменен');
                await loadContracts(); // Перезагружаем список
            } else {
                throw new Error(response.message || 'Ошибка отмены договора');
            }

        } catch (error: any) {
            console.error('Ошибка отмены договора:', error);
            toast.error(error.message || 'Ошибка отмены договора');
        }
    };

    const handleSignContract = (contractId: number) => {
        navigate(`/contract-signing/${contractId}`);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Загрузка договоров...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Заголовок */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Договоры поставки</h1>
                    <p className="mt-2 text-gray-600">
                        Управление договорами поставки для компании {company?.name || 'Загрузка...'}
                    </p>
                </div>

                {/* Статистика */}
                {stats && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                        <span className="text-blue-600 font-semibold">{stats.total}</span>
                                    </div>
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-500">Всего договоров</p>
                                    <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                        <span className="text-gray-600 font-semibold">{stats.draft}</span>
                                    </div>
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-500">Черновики</p>
                                    <p className="text-2xl font-semibold text-gray-900">{stats.draft}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                                        <span className="text-yellow-600 font-semibold">{stats.pendingSignature}</span>
                                    </div>
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-500">Ожидают подписания</p>
                                    <p className="text-2xl font-semibold text-gray-900">{stats.pendingSignature}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                        <span className="text-green-600 font-semibold">{stats.signed}</span>
                                    </div>
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-500">Подписаны</p>
                                    <p className="text-2xl font-semibold text-gray-900">{stats.signed}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Кнопка создания договора */}
                <div className="mb-6">
                    <Button
                        onClick={handleCreateContract}
                        disabled={generating}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium"
                    >
                        {generating ? 'Создание...' : 'Создать новый договор'}
                    </Button>
                </div>

                {/* Список договоров */}
                <div className="bg-white shadow rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">Список договоров</h3>
                    </div>

                    {contracts.length === 0 ? (
                        <div className="px-6 py-12 text-center">
                            <div className="text-gray-400 mb-4">
                                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Договоры не найдены</h3>
                            <p className="text-gray-500 mb-4">Создайте первый договор поставки для вашей компании</p>
                            <Button
                                onClick={handleCreateContract}
                                disabled={generating}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                            >
                                {generating ? 'Создание...' : 'Создать договор'}
                            </Button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Номер договора
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Статус
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Дата создания
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Действия
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {contracts.map((contract) => (
                                        <tr key={contract.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {contract.contractNumber}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <StatusBadge
                                                    status={contract.status}
                                                    text={contractService.getStatusDisplayName(contract.status)}
                                                    className={contractService.getStatusColor(contract.status)}
                                                />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {new Date(contract.createdAt).toLocaleDateString('ru-RU')}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex space-x-2">
                                                    {contract.status === 'DRAFT' && (
                                                        <Button
                                                            onClick={() => handleGeneratePdf(contract.id)}
                                                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs"
                                                        >
                                                            Сгенерировать PDF
                                                        </Button>
                                                    )}

                                                    {contractService.canDownloadContract(contract) && (
                                                        <Button
                                                            onClick={() => handleDownloadContract(contract.id, contract.contractNumber)}
                                                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs"
                                                        >
                                                            Скачать
                                                        </Button>
                                                    )}

                                                    {contractService.canSignContract(contract.status) && (
                                                        <Button
                                                            onClick={() => handleSignContract(contract.id)}
                                                            className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded text-xs"
                                                        >
                                                            Подписать
                                                        </Button>
                                                    )}

                                                    {contractService.canCancelContract(contract.status) && (
                                                        <Button
                                                            onClick={() => handleCancelContract(contract.id)}
                                                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs"
                                                        >
                                                            Отменить
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
